import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { healthOfIssue } from '../progress-health';
import { effectiveProgress } from '../progress-derive';
import { ProgressMini } from './ProgressMini';
import { DataBar, type BarMarker } from './DataBar';
import { useApp } from '../store';
import { useSelection } from '../selection';
import type { Issue, IssueKind, IssueStatus, IssuePriority } from '../types';
import { STATUS_LABELS, STATUS_ORDER, PRIORITY_ORDER, PRIORITY_LABELS, liveAreas } from '../types';
import { statusOrderFor, isProjectMode, boardColumnsFor } from '../lifecycle';
import { stillBlocked } from '../blocked';
import { displayName } from '../people';
import { initials, relativeTime, asDate, asDayMonth } from '../utils';
import { inSprint, inOrder as sprintsInOrder, timeLeft, SPRINT_STATE_LABEL } from '../sprints';
import type { Sprint } from '../sprints';
import { inView, passes, viewsFor, hidesDone } from '../views';
import { livePhases, type Phase } from '../phases';
import { liveTypes, labelOf } from '../issue-types';
import { nextAreaColour } from '../area-colours';
import { assistantsOf, assistantAssignee, assigneeAssistant, assigneeName } from '../assistant';
import { boardAssignees } from '../projects';
import { can } from '../permissions';
import { ownedByBoard, type IssueSource } from '../ownership';
import Icon from './Icon';
import Priority, { PriorityGlyph } from './Priority';
import PickField, { type PickOption } from './PickField';
import AreaField from './AreaField';
import EpicField from './EpicField';
import VendorTag from './VendorTag';
import { DoerPrompt } from './DoerPrompt';
import { BacklogFilters } from './BacklogFilters';

/**
 * THE GRID — the board shown as a dense, editable table (#508 Phase 1). The same
 * cards as the kanban board, one per row, every field a column, so a whole board
 * can be scanned and edited without opening a card. It is a VIEW MODE of the
 * Board, not a screen of its own: the Board⇄Grid switch lives in the board header
 * (Board.tsx) and swaps the layout under it.
 *
 * What it shares with the rest of the app, on purpose, so nothing forks:
 *  · the Backlog's saved-view tabs and Filter (the same filters.ts / views.ts
 *    state — a filter set on the Backlog is the same set here);
 *  · the card's OWN field controls for editing in place — PickField for Status,
 *    Phase, Type and Assignee; AreaField and EpicField (type-to-create) for Area
 *    and Epic — so a value can only ever be one of the board's real values;
 *  · relativeTime for the Updated column, the one the Backlog already reads by.
 *
 * Keyboard-first (Vinnie pulled this into Phase 1): the focused cell is a
 * SELECTION, drawn distinct from editing; arrows move it, Tab / Shift+Tab walk
 * field by field in reading order (right, then on to the next row — the way a
 * spreadsheet moves, and leaving the grid at the two ends), Enter/F2 opens the
 * cell's editor (the same control a click opens), the Title cell's Enter opens
 * the card. Read-only cells (Priority, Due date, Updated) can be focused but
 * never open an editor.
 */

const NO_EPIC = 'Unsorted';

type ColKey =
  | 'title' | 'status' | 'progress' | 'priority' | 'phase' | 'epic'
  | 'area' | 'type' | 'assignee' | 'due' | 'updated';

interface Col {
  key: ColKey;
  label: string;
  w: number;
  /** Opens an in-cell editor on click / Enter. */
  editable: boolean;
  /** The quieter cell tone the dense table uses for plain-text values. */
  muted?: boolean;
  /** Shown only on a project-run board (the standing project-only rule). */
  projectOnly?: boolean;
}

const COLS: Col[] = [
  { key: 'title', label: 'Title', w: 320, editable: false },
  { key: 'status', label: 'Status', w: 132, editable: true },
  { key: 'progress', label: 'Progress', w: 150, editable: true },
  { key: 'priority', label: 'Priority', w: 100, editable: true, muted: true },
  { key: 'phase', label: 'Phase', w: 108, editable: true, muted: true, projectOnly: true },
  { key: 'epic', label: 'Epic', w: 158, editable: true },
  { key: 'area', label: 'Area', w: 150, editable: true },
  { key: 'type', label: 'Type', w: 96, editable: true, muted: true },
  { key: 'assignee', label: 'Accountable', w: 150, editable: true },
  { key: 'due', label: 'Due date', w: 120, editable: false },
  { key: 'updated', label: 'Updated', w: 112, editable: false },
];

const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;

/** The card is finished, so its target date is not a thing still approaching. */
const terminal = (s: IssueStatus) => s === 'done' || s === 'wontdo';

/** A YYYY-MM-DD day as the app's one written date — "30 Sep 2026". */
function dayWritten(iso: string): string {
  const t = new Date(`${iso}T00:00:00`).getTime();
  return Number.isNaN(t) ? iso : asDate(t);
}


/** The external-link mark on the Title cell — "open the full card". Decorative. */
function OpenGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4.5h5.5V10" />
      <path d="M19.5 4.5L11 13" />
      <path d="M17 13.5V19H5V7h5.5" />
    </svg>
  );
}

export default function GridView() {
  const issues = useApp((s) => s.issues);
  const search = useApp((s) => s.search);
  const meta = useApp((s) => s.meta);
  const role = useApp((s) => s.role);
  const source = useApp((s) => s.meta.source ?? 'tracker') as IssueSource;
  const areas = useApp((s) => s.areas);
  const issueTypes = useApp((s) => s.issueTypes);
  const phases = useApp((s) => s.phases);
  const profiles = useApp((s) => s.profiles);
  // The workspace working calendar (0216) — the progress column counts working days.
  const workCalendar = useApp((s) => s.workCalendar);
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const sprints = useApp((s) => s.sprints);
  const access = useApp((s) => s.access);
  const groupsOf = useApp((s) => s.groupsOf);
  const me = useApp((s) => s.user?.email ?? '');
  const filters = useApp((s) => s.filters);
  const setFilters = useApp((s) => s.setFilters);
  const savedViews = useApp((s) => s.savedViews);
  const viewId = useApp((s) => s.viewId);
  const setViewId = useApp((s) => s.setViewId);
  const saveView = useApp((s) => s.saveView);
  const forgetView = useApp((s) => s.forgetView);
  const openIssue = useApp((s) => s.openIssue);
  const moveIssue = useApp((s) => s.moveIssue);
  const updateIssue = useApp((s) => s.updateIssue);
  const createArea = useApp((s) => s.createArea);

  // ── Bulk selection (project-bulk-card-changes). The Grid mounts the SAME store
  // and the same BulkBar as the board, so it is bulk-edit for free. In select mode
  // the leading cell carries the box and the whole row is the hit target; the header
  // checkbox is select-all. Out of select mode the grid behaves exactly as today.
  const selIds = useSelection((s) => s.ids);
  const selectMode = useSelection((s) => s.selectMode);
  const toggleMode = useSelection((s) => s.toggleMode);
  const toggleSel = useSelection((s) => s.toggle);
  const rangeSel = useSelection((s) => s.rangeTo);
  const addSel = useSelection((s) => s.add);
  const removeSel = useSelection((s) => s.remove);
  const setSelOrder = useSelection((s) => s.setOrder);

  const isProject = isProjectMode(meta);
  const stagingOn = meta.stagingEnabled === true;
  const canEdit = can(role, 'editIssue');
  const canMove = can(role, 'moveIssue');
  const mineToEdit = (f: keyof Issue) => ownedByBoard(f, source);

  const cols = useMemo(() => COLS.filter((c) => !c.projectOnly || isProject), [isProject]);

  // THE GRID IS THE BOARD AS A TABLE, so it shows exactly the cards the kanban
  // draws — the board's own columns (To Do, In Progress, In Review, Done, and
  // In Staging where it is on) — and never the Backlog or Won't Do, neither of
  // which is a board column (BOARD_COLUMNS). The Backlog is its own screen; a
  // declined card is off the board. `boardColumnsFor` is the very predicate the
  // board uses, so the two can never disagree about which cards exist (Vinnie,
  // 19 Oct). Every count, view and filter below reads this set, not all issues.
  const onBoard = useMemo(() => new Set(boardColumnsFor(meta)), [meta]);
  const boardIssues = useMemo(() => issues.filter((i) => onBoard.has(i.status)), [issues, onBoard]);

  // ── WHICH SPRINT'S CARDS THE GRID SHOWS — the same picker the kanban board
  // carries, because the Grid is that board as a table and a sprint focus should
  // mean the same thing in both. It shares the board's per-person, per-board
  // memory (the localStorage key below), so picking a sprint on the board and
  // flipping to the Grid keeps it, and the other way round. "All sprints" (the
  // empty string) is the default that filters nothing; the picker only exists on
  // a board that works in sprints (Vinnie, 19 Oct).
  const sprintable = meta.cadence === 'sprints' && sprints.length > 0;
  const sprintKey = `flightdeck-board-sprint-${projectId}`;
  const [sprintPick, setSprintPick] = useState(() => {
    try {
      return localStorage.getItem(sprintKey) ?? '';
    } catch {
      return '';
    }
  });
  const pickSprint = (id: string) => {
    setSprintPick(id);
    try {
      if (id) localStorage.setItem(sprintKey, id);
      else localStorage.removeItem(sprintKey);
    } catch {
      /* private browsing — the pick just won't outlive the page */
    }
  };
  // A stored id whose sprint has since been deleted falls back to all, not to an
  // empty grid — the same fallback the board makes.
  const pickedSprint =
    sprintPick && meta.cadence === 'sprints' ? sprints.find((s) => s.id === sprintPick) : undefined;
  const [sprintOpen, setSprintOpen] = useState(false);
  const [sprintQuery, setSprintQuery] = useState('');
  const sprintWrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sprintOpen) setSprintQuery('');
  }, [sprintOpen]);
  useEffect(() => {
    if (!sprintOpen) return;
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.board-sprint-wrap')) setSprintOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSprintOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [sprintOpen]);
  // The board's working set once the sprint focus is applied. Everything that
  // represents "what's on the board right now" — the view, its filters, the tab
  // counts, the people who can be filtered by — reads this, exactly as the kanban
  // board narrows to `inSprint(visible, …)`. The true board set (boardIssues)
  // stays for the sprint menu's own counts and the "board is empty" message.
  const scoped = useMemo(
    () => (pickedSprint ? inSprint(boardIssues, pickedSprint.id) : boardIssues),
    [boardIssues, pickedSprint],
  );
  // "13–27 Oct", "29 Sep–12 Oct" — the sprint's dates, the month shown once when
  // start and end share it.
  const sprintRange = (s: Sprint) => {
    const start = asDayMonth(s.startsOn);
    const end = asDayMonth(s.endsOn);
    const sameMonth = start.split(' ')[1] === end.split(' ')[1];
    return sameMonth ? `${start.split(' ')[0]}–${end}` : `${start}–${end}`;
  };

  // ── The row set: the chosen view, then the filters, then the global search —
  // the same order and the same helpers the Backlog uses, so the two lists never
  // disagree about what is on the board.
  const views = useMemo(() => viewsFor(savedViews, projectId), [savedViews, projectId]);
  const view = views.find((v) => v.id === viewId) ?? views[0];
  const inThisView = useMemo(() => inView(scoped, view, me, NO_EPIC), [scoped, view, me]);
  const nameOf = (email: string) => displayName(email, profiles);

  const q = search.trim().toLowerCase();
  const matchesSearch = (i: Issue) =>
    !q ||
    i.title.toLowerCase().includes(q) ||
    i.ref.toLowerCase().includes(q) ||
    (i.epic ?? '').toLowerCase().includes(q) ||
    i.labels.some((l) => l.toLowerCase().includes(q));

  const filtered = useMemo(
    () => inThisView.filter((i) => matchesSearch(i) && passes(i, view?.filters ?? filters, NO_EPIC)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inThisView, view, filters, q],
  );
  const hiddenByFilter = inThisView.filter(matchesSearch).length - filtered.length;

  const areaOf = (i: Issue) => areas.find((a) => a.id === i.area);
  const phaseOf = (i: Issue) => phases.find((p) => p.id === i.phase);
  const assigneeLabel = (k: string) => assigneeName(k, meta, (e) => displayName(e, profiles));

  // Who can be filtered by in the bar: the people who actually hold work here —
  // the same set the Backlog's Filter offers (not the whole membership list).
  const people = useMemo(
    () => [...new Set(scoped.map((i) => i.assignee).filter((e): e is string => !!e))].sort((a, b) => displayName(a, profiles).localeCompare(displayName(b, profiles))),
    [scoped, profiles],
  );

  // ── Sort: one column at a time, driven by clicking its header (Phase 1). The
  // default is by status, the order the board itself reads in.
  const [sort, setSort] = useState<{ key: ColKey; dir: 1 | -1 }>({ key: 'status', dir: 1 });
  const sortValue = (i: Issue, key: ColKey): string | number => {
    switch (key) {
      case 'title': return i.title.toLowerCase();
      case 'status': return STATUS_ORDER.indexOf(i.status);
      case 'progress': return i.progressPct ?? -1;
      case 'priority': return PRIORITY_ORDER.indexOf(i.priority);
      case 'phase': return phaseOf(i)?.name?.toLowerCase() ?? '￿';
      case 'epic': return (i.epic || '￿').toLowerCase();
      case 'area': return areaOf(i)?.name?.toLowerCase() ?? '￿';
      case 'type': return labelOf(i.kind, issueTypes).toLowerCase();
      case 'assignee': return i.assignee ? assigneeLabel(i.assignee).toLowerCase() : '￿';
      case 'due': return i.dueDate || '￿';
      case 'updated': return i.updatedAt;
      default: return '';
    }
  };
  const rows = useMemo(() => {
    const out = [...filtered];
    out.sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (va < vb) return -1 * sort.dir;
      if (va > vb) return 1 * sort.dir;
      // A stable tiebreak so equal cells keep a predictable order.
      return a.ref.localeCompare(b.ref) * sort.dir;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, areas, phases, issueTypes, profiles]);

  const clickHeader = (key: ColKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  // Feed the selection store the grid's flat row order, so a Shift-click ranges by
  // the order shown here, exactly as it does on the board.
  useEffect(() => {
    if (selectMode) setSelOrder(rows.map((i) => i.id));
  }, [rows, selectMode, setSelOrder]);

  // Clicking a row in select mode ticks it (Shift extends a range); the leading
  // cell carries the box, but the whole row is the target.
  const rowIds = useMemo(() => rows.map((i) => i.id), [rows]);
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selIds.has(id));
  const toggleRow = (id: string, shift: boolean) => {
    // Set the order to what is shown RIGHT NOW (this sort + filter), synchronously,
    // so a Shift-range is measured against the CURRENT displayed rows and never a
    // stale order — it must tick every row between the anchor and here.
    setSelOrder(rowIds);
    if (shift) {
      // A Shift-click ranges the ROWS; clear any text selection the gesture may
      // have started so no blue highlight is left across the cells.
      window.getSelection()?.removeAllRanges();
      rangeSel(id);
    } else {
      toggleSel(id);
    }
  };
  const toggleAllRows = () => (allSelected ? removeSel(rowIds) : addSel(rowIds));

  // ── Focus & editing. One cell is the selection (roving tabindex); editing is a
  // separate state — the cell's own editor, open. The selection is held by the
  // card's ID, not its row number, so an edit that re-sorts the rows (changing a
  // status while sorted by status) keeps the selection on the same card instead of
  // jumping to whatever slid into that slot.
  const [focusId, setFocusId] = useState<string | null>(null);
  const [focusCol, setFocusCol] = useState(0);
  const [editing, setEditing] = useState(false);
  // DOER CAPTURE, THE GRID PATH (A2, F1). Setting a card's Status to In Progress
  // in the grid, when it has no doer, asks who's doing it — the same prompt the
  // board shows, anchored to the row. The grid has no Responsible column, so the
  // popover is the right surface here. Asked once per card + mover.
  const [doerAsk, setDoerAsk] = useState<Issue | null>(null);
  const dismissedDoer = useRef<Set<string>>(new Set());
  const cellRef = useRef<HTMLDivElement>(null);
  // Only move real DOM focus once the person has actually driven the grid, so
  // mounting it never steals focus or scrolls the page to the first row.
  const driven = useRef(false);

  const effectiveId = focusId && rows.some((i) => i.id === focusId) ? focusId : (rows[0]?.id ?? null);
  const focusRow = Math.max(0, rows.findIndex((i) => i.id === effectiveId));
  const focusC = Math.min(focusCol, cols.length - 1);

  // The onClose callback the editors fire runs a tick after a commit has already
  // re-sorted the rows, so it must read the CURRENT rows and selection, not the
  // ones captured when the editor was built. These refs carry the latest.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const focusIdRef = useRef<string | null>(effectiveId);
  focusIdRef.current = effectiveId;
  const focusColRef = useRef(focusC);
  focusColRef.current = focusC;

  useEffect(() => {
    if (!driven.current || editing) return;
    const cell = cellRef.current;
    if (!cell) return;
    // Focus WITHOUT the browser's own scroll-into-view, then scroll by hand. The
    // Title column is position:sticky on the left and the header row sticky on top,
    // so each floats OVER the edge of the scroller. A cell scrolled under them still
    // counts as on-screen to the browser, so moving LEFT (or up) never scrolled the
    // board back — the focused cell just slid behind the Title until you reached it
    // (Vinnie, 19 Oct). Scrolling by hand keeps the cell clear of both.
    cell.focus({ preventScroll: true });
    const scroller = cell.closest('.gv-scroll') as HTMLElement | null;
    if (!scroller) return;
    const cR = cell.getBoundingClientRect();
    const sR = scroller.getBoundingClientRect();
    if (cell.closest('.gv-sticky')) {
      // The Title cell is itself sticky (always visible); bring the row back to the
      // start so the columns after it read left-to-right again.
      scroller.scrollLeft = 0;
    } else {
      const sticky = scroller.querySelector('tbody .gv-sticky') as HTMLElement | null;
      const leftBound = sR.left + (sticky ? sticky.getBoundingClientRect().width : 0);
      if (cR.left < leftBound) scroller.scrollLeft -= leftBound - cR.left;
      else if (cR.right > sR.right) scroller.scrollLeft += cR.right - sR.right;
    }
    const head = scroller.querySelector('thead') as HTMLElement | null;
    const topBound = sR.top + (head ? head.getBoundingClientRect().height : 0);
    if (cR.top < topBound) scroller.scrollTop -= topBound - cR.top;
    else if (cR.bottom > sR.bottom) scroller.scrollTop += cR.bottom - sR.bottom;
  }, [focusId, focusCol, editing]);

  const moveFocus = (dr: number, dc: number) => {
    driven.current = true;
    const rws = rowsRef.current;
    const curRow = Math.max(0, rws.findIndex((i) => i.id === focusIdRef.current));
    const nr = Math.min(Math.max(0, curRow + dr), Math.max(0, rws.length - 1));
    setFocusId(rws[nr]?.id ?? null);
    setFocusCol((pc) => Math.min(Math.max(0, pc + dc), cols.length - 1));
  };

  // Tab / Shift+Tab walk the cells in reading order — right along the row, then
  // on to the start of the next row — so the whole grid can be crossed field by
  // field from the keyboard, the way a spreadsheet moves. Clamped at the two
  // ends; the caller lets Tab actually LEAVE the grid there rather than trapping
  // the keyboard inside it.
  const moveFocusLinear = (step: number) => {
    driven.current = true;
    const rws = rowsRef.current;
    const n = cols.length;
    const curRow = Math.max(0, rws.findIndex((i) => i.id === focusIdRef.current));
    const curCol = Math.min(Math.max(0, focusColRef.current), n - 1);
    const flat = Math.min(Math.max(0, curRow * n + curCol + step), Math.max(0, rws.length * n - 1));
    setFocusId(rws[Math.floor(flat / n)]?.id ?? null);
    setFocusCol(flat % n);
  };

  const enterEdit = (i: Issue, col: Col) => {
    if (col.key === 'title') { openIssue(i.id); return; }
    if (!cellIsEditable(col)) return;
    driven.current = true;
    setEditing(true);
  };

  // Commit a value. We do NOT leave edit mode here: that is the editor's own
  // onClose a tick later, which also moves the selection. Leaving early would
  // unmount the editor before onClose could fire, so the selection would never
  // step on. The editor is anchored by card ID, so even when this write re-sorts
  // the rows it stays on the SAME card (never the one that slid into its slot).
  const commit = (fn: () => void) => { fn(); };

  // Leaving an editor: the commit (if any) already happened; here we only move the
  // selection — a pick steps down, Tab across, Escape stays put (frame 07).
  const afterEdit = (reason: 'pick' | 'cancel' | 'away' | 'next' | 'prev') => {
    setEditing(false);
    driven.current = true;
    if (reason === 'pick') moveFocus(1, 0);
    else if (reason === 'next') moveFocusLinear(1);
    else if (reason === 'prev') moveFocusLinear(-1);
    // 'cancel' / 'away' → the same cell keeps the selection.
  };

  const onCellKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>, i: Issue, col: Col) => {
    if (editing) return;
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); moveFocus(1, 0); break;
      case 'ArrowUp': e.preventDefault(); moveFocus(-1, 0); break;
      case 'ArrowRight': e.preventDefault(); moveFocus(0, 1); break;
      case 'ArrowLeft': e.preventDefault(); moveFocus(0, -1); break;
      case 'Home': e.preventDefault(); setFocusCol(0); break;
      case 'End': e.preventDefault(); setFocusCol(cols.length - 1); break;
      case 'Tab': {
        // Step to the next / previous field, but at the very first and last cell
        // let Tab LEAVE the grid rather than trapping the keyboard in it.
        const rws = rowsRef.current;
        const n = cols.length;
        const curRow = Math.max(0, rws.findIndex((x) => x.id === focusIdRef.current));
        const curCol = Math.min(Math.max(0, focusColRef.current), n - 1);
        const flat = curRow * n + curCol;
        if (e.shiftKey ? flat <= 0 : flat >= rws.length * n - 1) break;
        e.preventDefault();
        moveFocusLinear(e.shiftKey ? -1 : 1);
        break;
      }
      case 'Enter':
      case 'F2': e.preventDefault(); enterEdit(i, col); break;
      default: break;
    }
  };

  // ── The editor for an editable cell, mounted already open (autoOpen) so one
  // click or Enter lands straight in the list. Same component the card uses.
  const editorFor = (i: Issue, col: Col) => {
    const common = { autoOpen: true, onClose: afterEdit } as const;
    if (col.key === 'status') {
      const choices = statusOrderFor({ stagingEnabled: stagingOn || i.status === 'staging' });
      const options: PickOption[] = choices.map((s) => ({
        value: s, label: STATUS_LABELS[s],
        mark: <span className={`status-dot dot-${s}`} aria-hidden="true" />,
      }));
      return <PickField ariaLabel="Status" value={i.status} options={options} onChange={(v) => commit(() => {
        const next = v as IssueStatus;
        void moveIssue(i.id, next);
        // Work is starting — ask who's doing it, once, if no doer is named yet.
        if (next === 'inprogress' && canEdit && me && !(i.responsible ?? '').trim() && !dismissedDoer.current.has(`${i.id}|${me}`)) {
          setDoerAsk(i);
        }
      })} {...common} />;
    }
    if (col.key === 'priority') {
      // The very picker the card uses — PriorityGlyph marks, the same labels —
      // so changing a priority in the grid reads identically to changing it on
      // the card (Vinnie, 20 Oct: Enter on Priority must open a menu like the rest).
      const options: PickOption[] = PRIORITY_ORDER.map((p) => ({
        value: p, label: PRIORITY_LABELS[p], mark: <PriorityGlyph priority={p} />,
      }));
      return <PickField ariaLabel="Priority" value={i.priority} options={options} onChange={(v) => commit(() => updateIssue(i.id, { priority: v as IssuePriority }))} {...common} />;
    }
    if (col.key === 'type') {
      const live = liveTypes(issueTypes);
      const options: PickOption[] = [
        ...live.map((t) => ({ value: t.slug, label: t.name })),
        ...(i.kind && !live.some((t) => t.slug === i.kind)
          ? [{ value: i.kind, label: `${labelOf(i.kind, issueTypes)} (retired)` }] : []),
      ];
      return <PickField ariaLabel="Type" value={i.kind} options={options} onChange={(v) => commit(() => updateIssue(i.id, { kind: v as IssueKind }))} {...common} />;
    }
    if (col.key === 'phase') {
      const live = livePhases(phases);
      const mineNow = phases.find((p) => p.id === i.phase);
      const offerable = mineNow && mineNow.retiredAt ? [...live, mineNow] : live;
      const options: PickOption[] = [
        { value: '', label: 'No phase' },
        ...offerable.map((p) => ({ value: p.id, label: `${p.name}${p.retiredAt ? ' (retired)' : ''}` })),
      ];
      return <PickField ariaLabel="Phase" value={i.phase ?? ''} options={options} onChange={(v) => commit(() => updateIssue(i.id, { phase: v || null }))} {...common} />;
    }
    if (col.key === 'assignee') {
      const assignable = boardAssignees(projects.find((p) => p.id === projectId), access.emails, access.admins, groupsOf);
      const options: PickOption[] = [
        { value: '', label: 'Unassigned' },
        ...assignable.map((m) => ({ value: m, label: displayName(m, profiles), trailing: <VendorTag who={m} profiles={profiles} compact /> })),
        ...assistantsOf(meta).map((a) => ({ value: assistantAssignee(a.id), label: a.name?.trim() || a.id, group: 'Assistants' })),
        ...(i.assignee && !assignable.includes(i.assignee) && !assigneeAssistant(i.assignee, meta)
          ? [{ value: i.assignee, label: i.assignee, trailing: <VendorTag who={i.assignee} profiles={profiles} compact /> }] : []),
      ];
      return <PickField ariaLabel="Accountable" value={i.assignee} options={options} onChange={(v) => commit(() => updateIssue(i.id, { assignee: v }))} {...common} />;
    }
    if (col.key === 'area') {
      const live = liveAreas(areas);
      const mineNow = areas.find((a) => a.id === i.area);
      const offerable = mineNow && mineNow.retiredAt ? [...live, mineNow] : live;
      return (
        <AreaField
          value={i.area ?? ''}
          areas={offerable}
          disabled={false}
          onChange={(id) => commit(() => updateIssue(i.id, { area: id || null }))}
          onCreate={(name) => createArea({ name, colour: nextAreaColour(areas) })}
          {...common}
        />
      );
    }
    if (col.key === 'epic') {
      const epicOptions = [...new Set(issues.map((x) => x.epic).filter(Boolean))].sort() as string[];
      return (
        <EpicField
          value={i.epic ?? ''}
          options={epicOptions}
          disabled={false}
          onChange={(epic) => commit(() => updateIssue(i.id, { epic }))}
          {...common}
        />
      );
    }
    if (col.key === 'progress') {
      // A focused number box with the live mini bar beside it — type a value, Enter
      // commits and steps to the next row, Escape cancels. Fast down a vendor's list.
      return (
        <GridProgressEdit
          issue={i}
          phase={phaseOf(i)}
          onCommit={(next) => commit(() => updateIssue(i.id, { progressPct: next }))}
          onClose={afterEdit}
        />
      );
    }
    return null;
  };

  // The read display inside a cell when it is not being edited.
  const display = (i: Issue, col: Col) => {
    switch (col.key) {
      case 'status':
        return <span className={`gv-status gv-status-${i.status}`}><span className={`status-dot dot-${i.status}`} aria-hidden="true" />{STATUS_LABELS[i.status]}</span>;
      case 'priority':
        return <Priority priority={i.priority} />;
      case 'phase': {
        const p = phaseOf(i);
        return p ? <span className="nm">{p.name}{p.retiredAt ? ' (retired)' : ''}</span> : <span className="gv-none">—</span>;
      }
      case 'epic':
        return i.epic ? <span className="epic-chip">{i.epic}</span> : <span className="gv-none">—</span>;
      case 'area': {
        const a = areaOf(i);
        return a
          ? <span className="area-chip"><span className="area-dot" style={{ background: a.colour }} /><span className="nm">{a.name}</span>{a.retiredAt ? ' (retired)' : ''}</span>
          : <span className="gv-none">—</span>;
      }
      case 'type':
        return <>{labelOf(i.kind, issueTypes)}</>;
      case 'assignee':
        return i.assignee
          ? <span className="gv-asg"><span className="avatar" aria-hidden="true">{initials(assigneeLabel(i.assignee))}</span><span className="nm">{assigneeLabel(i.assignee)}</span><VendorTag who={i.assignee} profiles={profiles} compact /></span>
          : <span className="gv-none">—</span>;
      case 'due': {
        if (!i.dueDate) return <span className="gv-none">—</span>;
        const t = new Date(`${i.dueDate}T00:00:00`).getTime();
        const soon = !terminal(i.status) && !Number.isNaN(t) && t <= Date.now() + TWO_WEEKS;
        return <span className={`gv-date${soon ? ' soon' : ''}`}>{dayWritten(i.dueDate)}</span>;
      }
      case 'updated': {
        const stale = Date.now() - i.updatedAt > TWO_WEEKS;
        return <span className={`gv-rel${stale ? ' stale' : ''}`}>{relativeTime(i.updatedAt)}</span>;
      }
      case 'progress': {
        // The EFFECTIVE % — typed when set, else derived from the board's ticked
        // Definition-of-Done — so a checklist-tracked card shows its derived bar.
        // A card with no date of its own falls back to its phase's window.
        const eff = effectiveProgress(i, meta.doneChecks);
        const h = healthOfIssue({ ...i, progressPct: eff.pct }, undefined, phaseOf(i), workCalendar);
        return h.kind === 'none' ? <span className="gv-none">—</span> : <ProgressMini health={h} className="gv-prog" source={eff.source} checklistCount={{ done: (i.checklist ?? []).filter((c) => c.done).length, total: (i.checklist ?? []).length }} />;
      }
      default: return null;
    }
  };

  const cellIsEditable = (col: Col) =>
    col.editable && (col.key === 'status' ? canMove && mineToEdit('status') : canEdit && mineToEdit(editField(col.key)));

  return (
    <div className="gv-view">
      <BacklogFilters
        issues={boardIssues}
        inThisView={inThisView}
        areas={areas}
        types={issueTypes}
        phases={phases}
        people={people}
        nameOf={nameOf}
        noEpic={NO_EPIC}
        filters={filters}
        onFilters={(f) => setFilters(f)}
        views={views}
        viewId={viewId}
        onView={setViewId}
        countInView={(v) => inView(scoped, v, me, NO_EPIC).length}
        groupBy="none"
        onGroupBy={() => {}}
        showCounts={false}
        onShowCounts={() => {}}
        onSaveView={saveView}
        onForgetView={forgetView}
        shown={rows.length}
        hiddenByFilters={hiddenByFilter}
        hidesDone={hidesDone(view)}
        showDisplay={false}
        extra={
          <>
            {/* SELECT — the same explicit multi-select toggle the board carries, so
                the Grid turns into a selection surface the same way (glyph only; its
                brand-tint fill and tooltip carry the meaning). */}
            <button
              type="button"
              className={`tb-btn board-select-toggle${selectMode ? ' is-on' : ''}`}
              aria-pressed={selectMode}
              aria-label={selectMode ? 'Done selecting' : 'Select rows'}
              title={selectMode ? 'Done selecting (S)' : 'Select rows (S)'}
              onClick={() => toggleMode()}
            >
              <svg className="ic" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="4" />
                <path d="M8 12.5l2.5 2.5L16 9" strokeWidth="2" />
              </svg>
            </button>
          {sprintable ? (
            // SPRINT FOCUS — the board's own picker, sat inline on the same line as
            // Filter (Vinnie, 20 Oct). Same classes as the board so it reads
            // identically; gv-sprint-wrap just flips the menu to drop down-and-left,
            // since it now lives at the right edge of the bar. Shown only on a board
            // that works in sprints.
            <div className="board-sprint-wrap gv-sprint-wrap" ref={sprintWrap}>
              <button
                className={`tb-btn${sprintOpen ? ' open' : ''}${pickedSprint ? ' is-set' : ''}`}
                aria-haspopup="menu"
                aria-expanded={sprintOpen}
                title="Show one sprint's cards"
                onClick={() => setSprintOpen((o) => !o)}
              >
                <Icon name="calendar" />
                {pickedSprint && <span className={`bs-dot ${pickedSprint.state}`} aria-hidden="true" />}
                <span className="board-focus-cur">{pickedSprint ? pickedSprint.name : 'All sprints'}</span>
                {pickedSprint && (
                  <span
                    className="board-focus-clear"
                    role="button"
                    tabIndex={0}
                    aria-label="Show all sprints again"
                    title="Show all sprints again"
                    onClick={(e) => {
                      e.stopPropagation();
                      pickSprint('');
                    }}
                  >
                    ✕
                  </span>
                )}
              </button>
              {sprintOpen && (
                <div className="board-sprint-menu" role="menu" aria-label="Show one sprint's cards">
                  <div className="board-sprint-find">
                    <input
                      className="board-focus-look"
                      value={sprintQuery}
                      onChange={(e) => setSprintQuery(e.target.value)}
                      placeholder="Find a sprint…"
                      aria-label="Find a sprint"
                      autoFocus
                    />
                  </div>
                  <button
                    role="menuitemradio"
                    aria-checked={!sprintPick}
                    className={`board-sprint-opt${!sprintPick ? ' on' : ''}`}
                    onClick={() => {
                      pickSprint('');
                      setSprintOpen(false);
                    }}
                  >
                    <span className="board-sprint-tick" aria-hidden="true">{!sprintPick ? '✓' : ''}</span>
                    <span className="board-focus-av all" aria-hidden="true">
                      <Icon name="board" size={12} />
                    </span>
                    <span className="board-sprint-nm">All sprints</span>
                    <span className="count">{boardIssues.length}</span>
                  </button>
                  <div className="board-focus-sep" role="separator" />
                  {(() => {
                    const query = sprintQuery.trim().toLowerCase();
                    const match = (s: Sprint) => !query || s.name.toLowerCase().includes(query);
                    const groups: { head: string; items: Sprint[] }[] = [
                      { head: SPRINT_STATE_LABEL.active, items: sprints.filter((s) => s.state === 'active' && match(s)) },
                      { head: SPRINT_STATE_LABEL.planned, items: sprintsInOrder(sprints.filter((s) => s.state === 'planned' && match(s))) },
                      { head: SPRINT_STATE_LABEL.closed, items: sprintsInOrder(sprints.filter((s) => s.state === 'closed' && match(s))) },
                    ];
                    const metaOf = (s: Sprint) =>
                      s.state === 'active'
                        ? `${timeLeft(s)} · ${sprintRange(s)}`
                        : s.state === 'planned'
                          ? `Starts ${asDayMonth(s.startsOn)}`
                          : sprintRange(s);
                    const shownGroups = groups.filter((g) => g.items.length);
                    if (query && shownGroups.length === 0) {
                      return <div className="board-sprint-none">No sprint matches “{sprintQuery}”.</div>;
                    }
                    return shownGroups.map((g) => (
                      <div key={g.head} className="board-sprint-sec">
                        <div className="board-sprint-group">{g.head}</div>
                        {g.items.map((s) => (
                          <button
                            key={s.id}
                            role="menuitemradio"
                            aria-checked={sprintPick === s.id}
                            className={`board-sprint-opt${sprintPick === s.id ? ' on' : ''}`}
                            onClick={() => {
                              pickSprint(s.id);
                              setSprintOpen(false);
                            }}
                          >
                            <span className="board-sprint-tick" aria-hidden="true">
                              {sprintPick === s.id ? '✓' : ''}
                            </span>
                            <span className="board-sprint-mark" aria-hidden="true">
                              <span className={`bs-dot ${s.state}`} />
                            </span>
                            <span className="board-sprint-nm">{s.name}</span>
                            {s.state === 'active' && <span className="bs-live">Live now</span>}
                            <span className="board-sprint-meta">{metaOf(s)}</span>
                            <span className="count">{inSprint(boardIssues, s.id).length}</span>
                          </button>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          ) : null}
          </>
        }
      />


      {rows.length === 0 ? (
        <div className={`gv-empty${boardIssues.length === 0 ? '' : ' gv-empty--anchored'}`}>
          <span className="glyph"><Icon name="table" size={30} /></span>
          {boardIssues.length === 0 ? (
            <>
              <h2>No cards on this board yet</h2>
              <p>The Grid shows every card on the board as a row you can scan and edit in place. Add your first card and it appears here.</p>
              <p className="steps">To add a card: <b>New issue</b> (top left) → give it a title → set its status and owner. Already have a list? <b>Settings → Import</b> brings a spreadsheet in.</p>
            </>
          ) : (
            <>
              <h2>Nothing matches this view</h2>
              <p>
                No cards match this view right now. Change the view above
                {pickedSprint ? <>, pick <b>All sprints</b></> : ''}, or clear any filters, to see more.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="gv-scroll">
          <table className={`gv-table${selectMode ? ' is-selectmode' : ''}`} role="grid" style={{ minWidth: cols.reduce((n, c) => n + c.w, 0) + (selectMode ? 44 : 0) }}>
            <thead className="gv-thead">
              <tr role="row">
                {/* The select column is always present so it can GLIDE in and out
                    with select mode (R30 parity); CSS collapses it to zero width out
                    of mode. Its control only acts, and only takes focus, while on. */}
                <th className="gv-th gv-th-sel gv-sticky-sel" scope="col" aria-hidden={!selectMode || undefined}>
                  <span
                    className={`col-selectall gv-selall${allSelected ? ' on' : ''}`}
                    role="checkbox"
                    aria-checked={allSelected}
                    tabIndex={selectMode ? 0 : -1}
                    aria-label={allSelected ? 'Deselect all rows' : 'Select all rows'}
                    title={allSelected ? 'Deselect every row' : 'Select every row'}
                    onClick={selectMode ? toggleAllRows : undefined}
                    onKeyDown={(e) => {
                      if (selectMode && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleAllRows(); }
                    }}
                  >
                    <svg className="ic" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                </th>
                {cols.map((col) => {
                  const sorted = sort.key === col.key;
                  return (
                    <th
                      key={col.key}
                      className={`gv-th${col.key === 'title' ? ' gv-sticky' : ''}${sorted ? ' sorted' : ''}${sorted && sort.dir === -1 ? ' desc' : ''}`}
                      style={{ width: col.w, minWidth: col.w }}
                      aria-sort={sorted ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
                    >
                      <button type="button" className="gv-th-inner" onClick={() => clickHeader(col.key)}>
                        {col.label}
                        <span className={`pf-sortmark dir-${sorted ? (sort.dir === 1 ? 'asc' : 'desc') : 'idle'}`} aria-hidden="true"><Icon name="chevron" size={16} /></span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((i, r) => {
                // stillBlocked, not blockerOf: a dependent whose predecessor is
                // Done (and a finished card) is no longer blocked, so no flag — in
                // step with the card face, metrics and the Blocked view (L160).
                const blocked = stillBlocked(i, issues);
                const rowSel = selIds.has(i.id);
                return (
                  <tr
                    role="row"
                    key={i.id}
                    data-issue-id={i.id}
                    className={`gv-row${i.status === 'done' ? ' is-done' : ''}${i.status === 'wontdo' ? ' is-wontdo' : ''}${selectMode ? ' is-selectmode' : ''}${rowSel ? ' is-selected' : ''}`}
                    aria-selected={selectMode ? rowSel : undefined}
                    onMouseDown={selectMode ? (e) => { if (e.shiftKey) e.preventDefault(); } : undefined}
                    onClick={selectMode ? (e) => toggleRow(i.id, e.shiftKey) : undefined}
                  >
                    {/* Always present (glides with the mode); CSS collapses it out
                        of select mode. */}
                    <td role="presentation" className="gv-selcell gv-sticky-sel" aria-hidden={!selectMode || undefined}>
                      <span className={`gv-selbox${rowSel ? ' on' : ''}`} aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                      </span>
                    </td>
                    {cols.map((col, c) => {
                      const isFocused = r === focusRow && c === focusC;
                      const isEditing = isFocused && editing;
                      const editable = cellIsEditable(col);
                      if (col.key === 'title') {
                        return (
                          <td role="presentation" key={col.key} className="gv-sticky" style={{ width: col.w, maxWidth: col.w }}>
                            <div
                              ref={isFocused && !editing ? cellRef : undefined}
                              className={`gv-title-cell${isFocused ? ' focused' : ''}`}
                              role="gridcell"
                              aria-selected={isFocused}
                              tabIndex={isFocused && !editing ? 0 : -1}
                              onClick={() => { driven.current = true; setFocusId(i.id); setFocusCol(c); }}
                              onKeyDown={(e) => onCellKeyDown(e, i, col)}
                            >
                              <div className="gv-title-top">
                                <span className="gv-ref">{i.ref}</span>
                                {blocked && <span className="flag-loz"><Icon name="flag" size={11} />Blocked</span>}
                                <span
                                  className="gv-title"
                                  onClick={(e) => { e.stopPropagation(); openIssue(i.id); }}
                                >{i.title}</span>
                                <span className="gv-open" title="Open card" onClick={(e) => { e.stopPropagation(); openIssue(i.id); }}><OpenGlyph /></span>
                              </div>
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td role="presentation" key={col.key} style={{ width: col.w, minWidth: col.w }}>
                          <div
                            ref={isFocused && !editing ? cellRef : undefined}
                            className={`gv-cell${editable ? ' editable' : ''}${col.muted ? ' muted' : ''}${isFocused && !editing ? ' focused' : ''}${isEditing ? ' editing' : ''}`}
                            role="gridcell"
                            aria-selected={isFocused}
                            tabIndex={isFocused && !editing ? 0 : -1}
                            onClick={() => { driven.current = true; setFocusId(i.id); setFocusCol(c); if (editable && !isEditing) setEditing(true); }}
                            onKeyDown={(e) => onCellKeyDown(e, i, col)}
                          >
                            {isEditing ? editorFor(i, col) : (
                              <>
                                {display(i, col)}
                                {editable && <span className="caret"><Icon name="chevron" size={16} /></span>}
                              </>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The doer-capture prompt, hanging off the row whose status just became
          In Progress with no doer (F1). Same component the board uses. */}
      {doerAsk && (
        <DoerPrompt
          issueId={doerAsk.id}
          issueRef={doerAsk.ref}
          suggest={doerAsk.assignee ?? ''}
          options={(() => {
            const assignable = boardAssignees(projects.find((p) => p.id === projectId), access.emails, access.admins, groupsOf);
            return [
              { value: '', label: 'Unassigned' },
              ...assignable.map((m) => ({ value: m, label: displayName(m, profiles), trailing: <VendorTag who={m} profiles={profiles} compact /> })),
              ...assistantsOf(meta).map((a) => ({ value: assistantAssignee(a.id), label: a.name?.trim() || a.id, group: 'Assistants' })),
              ...(doerAsk.responsible && !assignable.includes(doerAsk.responsible) && !assigneeAssistant(doerAsk.responsible, meta)
                ? [{ value: doerAsk.responsible, label: doerAsk.responsible, trailing: <VendorTag who={doerAsk.responsible} profiles={profiles} compact /> }]
                : []),
            ];
          })()}
          onPickMe={() => { void updateIssue(doerAsk.id, { responsible: me }); setDoerAsk(null); }}
          onPick={(who) => { void updateIssue(doerAsk.id, { responsible: who }); setDoerAsk(null); }}
          onDismiss={() => { dismissedDoer.current.add(`${doerAsk.id}|${me}`); setDoerAsk(null); }}
        />
      )}
    </div>
  );
}

/**
 * The grid's inline progress editor — a focused number box with the live mini bar
 * beside it. Commits on Enter (then steps to the next row) or on blur; Escape
 * cancels. A done-guard stops the blur that unmounting fires from committing twice.
 */
function GridProgressEdit({
  issue,
  phase,
  onCommit,
  onClose,
}: {
  issue: Issue;
  /** The card's phase, so the live preview judges a date-less card against the
   *  phase window — the same fallback the read cell uses. */
  phase?: Phase;
  onCommit: (next: number | null) => void;
  onClose: (reason: 'pick' | 'cancel' | 'away') => void;
}) {
  const workCalendar = useApp((s) => s.workCalendar);
  const [val, setVal] = useState(issue.progressPct == null ? '' : String(issue.progressPct));
  const done = useRef(false);
  const parse = (s: string): number | null =>
    s.trim() === '' ? null : Math.max(0, Math.min(100, Math.round(Number(s))));
  const finish = (commitIt: boolean, reason: 'pick' | 'cancel' | 'away') => {
    if (done.current) return;
    done.current = true;
    if (commitIt) {
      const next = parse(val);
      if (next !== (issue.progressPct ?? null)) onCommit(next);
    }
    onClose(reason);
  };
  const h = healthOfIssue({ ...issue, progressPct: parse(val) }, undefined, phase, workCalendar);
  const toneCls =
    h.tone === 'good' ? 'is-good' : h.tone === 'warn' ? 'is-warn' : h.tone === 'bad' ? 'is-bad' : 'is-tracking';
  return (
    <span className="gv-prog-edit">
      {h.kind !== 'none' && (
        <span className={`prog-mini ${toneCls}`} style={{ flex: '1 1 auto' }}>
          <span className="prog-dot" />
          <DataBar
            density="tile"
            className="prog-dbar"
            fill={{ to: h.pct ?? 0, tone: h.tone === 'good' ? 'good' : h.tone === 'warn' ? 'warn' : h.tone === 'bad' ? 'bad' : 'neutral' }}
            markers={
              h.expectedPct != null && h.kind !== 'tracking'
                ? [{ pct: h.expectedPct!, soft: true, tip: { title: `Plan: ${h.expectedPct}% by today`, what: `On today's plan you'd be at ${h.expectedPct}%.` } } as BarMarker]
                : []
            }
          />
        </span>
      )}
      <input
        className="input gv-prog-num"
        type="number"
        min={0}
        max={100}
        value={val}
        autoFocus
        aria-label="Progress percent"
        onChange={(e) => setVal(e.target.value)}
        onWheel={(e) => e.currentTarget.blur()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true, 'pick');
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finish(false, 'cancel');
          }
        }}
        onBlur={() => finish(true, 'away')}
      />
    </span>
  );
}

/** The Issue field a column edits, for the per-field board-ownership gate. */
function editField(key: ColKey): keyof Issue {
  switch (key) {
    case 'type': return 'kind';
    case 'priority': return 'priority';
    case 'phase': return 'phase';
    case 'epic': return 'epic';
    case 'area': return 'area';
    case 'assignee': return 'assignee';
    case 'status': return 'status';
    case 'progress': return 'progressPct';
    default: return 'title';
  }
}
